import { Box, Button, IconButton, Typography } from "@mui/material";
import axios from "axios";
import TablePreview from "components/UI/TablePreview";
import { fileDownload } from "shared/utils";
import { PdfToExcelConversionObject } from "types/models/PdfToExcelConversion";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DownloadIcon from '@mui/icons-material/Download';
import { ArrowForward } from "@mui/icons-material";
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';

type CheckSumResultProps = {
    resultTables: PdfToExcelConversionObject[] | undefined
    correctableTables: boolean[]
    colSets: Set<number>[]
    rowSets: Set<number>[]
    onTableCorrectionChangeHandler: (i: number, checked: boolean) => void
    checkSumId: number
    startNewCheckSum: () => void
}

const CheckSumResult = (props: CheckSumResultProps) => {
    const { t: tr } = useTranslation();

    const handleDownloadResult = async (table: string[][], formatClasses: number[][], fileName: String) => {
        const resultFile = await axios.post(`/quality-check/check-sum/excel-table/generate`, {
            tables: table,
            formatClasses: formatClasses
        }, { responseType: 'blob' });
        fileDownload(resultFile.data, fileName)
    };

    return (
        props.resultTables === undefined ?
            <Typography>{tr('checkSum.result.extracting')}</Typography> :
            <div>
                <Typography style={{ whiteSpace: 'pre-line' }} >
                    {tr('checkSum.result.bullet1')}
                    {tr('checkSum.result.bullet2')}
                    {tr('checkSum.result.bullet3')}
                    {tr('checkSum.result.bullet4')}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <Button
                        id="finish-button"
                        onClick={() => props.startNewCheckSum()}
                        sx={{ alignItems: "stretch" }}
                        endIcon={<ArrowForward />}
                    >
                        {tr('checkSum.result.finish')}
                    </Button>
                </Box>
                {props.resultTables?.map((table, i) => {
                    let isCorrected = false;
                    table.correctedFormatClasses.forEach((row, rowIndex) => {
                        if (row.some((ele => ele === 8 || ele === 7 || ele === 9))) {
                            isCorrected = true;
                        }
                    })

                    return (
                        <Box sx={{ marginBottom: "10px", border: "1px dashed black", backgroundColor: (props.correctableTables[i] ? "transparent" : "#EFEFEF") }}>
                            <Box sx={{ borderBottom: "1px dashed black", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Box>
                                    {table.downloadable &&
                                        <IconButton
                                            sx={{ marginLeft: "10px" }}
                                            onClick={() => handleDownloadResult(table.tables, table.formatClasses, table.fileName)}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    }
                                    <Typography component="div" sx={{ paddingLeft: "10px", display: "inline-block", verticalAlign: "middle" }}>
                                        {tr('checkSum.result.previewOriginal', { name: table.fileName })}
                                    </Typography>
                                </Box>
                                <Box>
                                    <IconButton
                                        sx={{ marginLeft: "10px" }}
                                        onClick={() => handleDownloadResult(table.correctedTables, table.correctedFormatClasses, table.fileName)}
                                    >
                                        <DownloadIcon />
                                    </IconButton>
                                    <Typography component="div" sx={{ paddingLeft: "10px", display: "inline-block", verticalAlign: "middle" }}>
                                        {tr('checkSum.result.previewCorrected', { name: `${table!.fileName}` })}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-around" }}>
                                <Box sx={{ margin: "10px", border: "2px solid #DFDFDF", display: "inline-block", overflowX: "scroll", maxWidth: "60%", boxSizing: "border-box" }}>
                                    <TablePreview formatClasses={table.formatClasses} table={table.tables} firstNumericColumnIndex={table.firstNumericColumnIndex} />
                                </Box>
                                <ArrowRightAltIcon sx={{ fontSize: "40px" }} />
                                {isCorrected &&
                                    <Box sx={{ margin: "10px", border: "2px solid #DFDFDF", display: "inline-block", overflowX: "scroll", maxWidth: "60%", boxSizing: "border-box" }}>
                                        <TablePreview formatClasses={table!.correctedFormatClasses} table={table!.correctedTables} firstNumericColumnIndex={table!.firstNumericColumnIndex} />
                                    </Box>
                                }
                                {(!isCorrected && table.sums.length > 0) &&
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                        <ErrorIcon />
                                        {tr('checkSum.result.noSummingErrors')}
                                    </Box>
                                }
                                {(!isCorrected && table.sums.length === 0) &&
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                        <ErrorIcon />
                                        {tr('checkSum.result.noSumDetected')}
                                    </Box>
                                }
                            </Box>
                            {i === props.resultTables!.length - 1 &&
                                <Typography variant="h6" style={{ whiteSpace: 'pre-line' }} sx={{ textAlign: "center" }}>
                                    {tr('checkSum.result.thanksTitle')}
                                    {tr('checkSum.result.thanksSubTitle')}
                                </Typography>}
                        </Box>
                    )
                })
                }
            </div>
    );

};

export default CheckSumResult;